import {
  ActivityPanel,
  Badge,
  Button,
  DashboardCard,
  DashboardGrid,
  PageHeader,
  SectionHeader,
  Separator,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableShell,
} from '@zafroi/ui-kit';
import {
  ArrowLeft,
  ArrowRight,
  LifeBuoy,
  Mail,
  MessageSquare,
  Phone,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useConversations,
  useCustomer,
  useSupportRequests,
} from '@/hooks/useEngageQueries';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import {
  channelKindLabel,
  conversationStatusVariants,
  customerLifecycleVariants,
  priorityVariants,
  supportRequestStatusVariants,
} from '@/types/status-variants';
import { relativeTime, shortDate } from '@/lib/format';

export function CustomerProfilePage() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const customerQuery = useCustomer(customerId);
  const conversationsQuery = useConversations();
  const supportQuery = useSupportRequests();

  const linkedConversations = (conversationsQuery.data ?? []).filter(
    (c) => c.customerId === customerId,
  );
  const linkedSupport = (supportQuery.data ?? []).filter(
    (s) => s.customerId === customerId,
  );

  return (
    <QueryStateGuard
      data={customerQuery.data}
      isLoading={customerQuery.isLoading}
      isError={customerQuery.isError}
      emptyTitle="Customer not found"
      emptyDescription="This customer record may have been removed."
    >
      {(customer) => (
        <div className="space-y-6">
          <PageHeader
            eyebrow={
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() => navigate('/customers')}
              >
                <ArrowLeft className="h-3 w-3" />
                Customers
              </button>
            }
            title={customer.name}
            description={customer.company ?? 'Individual customer'}
            actions={
              <>
                <Button variant="outline" size="sm">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Start conversation
                </Button>
                <Button size="sm">
                  <LifeBuoy className="h-3.5 w-3.5" />
                  Raise request
                </Button>
              </>
            }
            meta={
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  Lifecycle
                  <StatusBadge
                    status={customer.lifecycle}
                    variants={customerLifecycleVariants}
                  />
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span>
                  Primary channel{' '}
                  <Badge tone="neutral" withDot={false}>
                    {channelKindLabel[customer.primaryChannel] ??
                      customer.primaryChannel}
                  </Badge>
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span>
                  Joined{' '}
                  <span className="text-foreground">
                    {shortDate(customer.joinedAt)}
                  </span>
                </span>
              </div>
            }
          />

          <DashboardGrid columns={3}>
            <DashboardCard title="Contact" icon={Phone}>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd className="text-foreground">{customer.phone ?? '—'}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Email</dt>
                  <dd className="text-foreground">{customer.email ?? '—'}</dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Company</dt>
                  <dd className="text-foreground">{customer.company ?? '—'}</dd>
                </div>
              </dl>
            </DashboardCard>

            <DashboardCard title="Engagement" icon={MessageSquare}>
              <dl className="space-y-2 text-sm">
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Conversations</dt>
                  <dd className="font-semibold tnum text-foreground">
                    {customer.totalConversations}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Open support</dt>
                  <dd className="font-semibold tnum text-foreground">
                    {customer.openSupportRequests}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-muted-foreground">Last seen</dt>
                  <dd className="text-foreground">
                    {relativeTime(customer.lastInteractionAt)}
                  </dd>
                </div>
              </dl>
            </DashboardCard>

            <ActivityPanel
              title="Interaction timeline"
              items={[
                {
                  id: 'i1',
                  title: 'Reorder request via WhatsApp',
                  description: 'Asked for the same SKU mix as April',
                  timestamp: relativeTime(customer.lastInteractionAt),
                  icon: MessageSquare,
                  tone: 'primary',
                },
                {
                  id: 'i2',
                  title: 'Support request raised',
                  description: 'Damaged carton on May 14 shipment',
                  timestamp: '2d ago',
                  icon: LifeBuoy,
                  tone: 'warning',
                },
                {
                  id: 'i3',
                  title: 'Festive catalogue mailed',
                  description: 'Auto-dispatch from campaign workflow',
                  timestamp: '6d ago',
                  icon: Mail,
                  tone: 'accent',
                },
              ]}
              limit={5}
            />
          </DashboardGrid>

          <SectionHeader
            withDivider
            title="Linked conversations"
            description="Threads tied to this customer across every channel."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/conversations')}
              >
                Open inbox
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            }
          />
          {linkedConversations.length > 0 ? (
            <TableShell density="compact">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Channel</TableHead>
                    <TableHead>Subject</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedConversations.map((conv) => (
                    <TableRow
                      key={conv.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/conversations/${conv.id}`)}
                    >
                      <TableCell className="text-xs uppercase tracking-wider text-muted-foreground">
                        {channelKindLabel[conv.channel] ?? conv.channel}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium text-foreground">
                          {conv.subject ?? 'No subject'}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {conv.preview}
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={conv.status}
                          variants={conversationStatusVariants}
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tnum">
                        {relativeTime(conv.lastMessageAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          ) : (
            <p className="text-sm text-muted-foreground">
              No conversations linked to this customer yet.
            </p>
          )}

          <SectionHeader
            withDivider
            title="Linked support requests"
            description="Incoming issues raised by this customer."
            actions={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/support')}
              >
                View all
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            }
          />
          {linkedSupport.length > 0 ? (
            <TableShell density="compact">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Subject</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {linkedSupport.map((req) => (
                    <TableRow
                      key={req.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/support/${req.id}`)}
                    >
                      <TableCell className="text-sm font-medium text-foreground">
                        {req.subject}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={req.priority}
                          variants={priorityVariants}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={req.status}
                          variants={supportRequestStatusVariants}
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tnum">
                        {relativeTime(req.createdAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableShell>
          ) : (
            <p className="text-sm text-muted-foreground">
              This customer has no open support requests.
            </p>
          )}
        </div>
      )}
    </QueryStateGuard>
  );
}
